import type {
  AlinhamentoBlocoSentenca,
  BlocoTextoSentenca,
  CapituloSentenca,
  SentencaArbitralDados,
  TrechoSentenca,
} from '../types/sentencaArbitral.types';
import type { SentencaArbitralDto, TrechoSentencaDto } from '../types/sentencaArbitralDto.schema';

// DTO validado (Zod .strict() no boundary) → modelo da view. Campo a campo de propósito
// (molde certificadoAcessosMapper): spread esconderia o dia em que o contrato e a view
// divergirem, e a identidade do DTO precisa de produtor explícito no detalhe. Opcional ausente
// fica ausente: é ele que distingue a sentença v1 da v2 na renderização.

type CapituloDto = SentencaArbitralDto['capitulos'][number];

const mapTrecho = (trecho: TrechoSentencaDto): TrechoSentenca => ({
  texto: trecho.texto,
  estilo: trecho.estilo,
  ...(trecho.href === undefined ? {} : { href: trecho.href }),
  ...(trecho.marcas === undefined ? {} : { marcas: trecho.marcas }),
});

const comAlinhamento = (
  alinhamento: AlinhamentoBlocoSentenca | undefined,
): { alinhamento?: AlinhamentoBlocoSentenca } => (alinhamento === undefined ? {} : { alinhamento });

const mapBloco = (bloco: CapituloDto['blocos'][number]): BlocoTextoSentenca => {
  switch (bloco.tipo) {
    case 'subtitulo':
      return {
        id: bloco.id,
        tipo: 'subtitulo',
        texto: bloco.texto,
        ...comAlinhamento(bloco.alinhamento),
      };
    case 'paragrafo':
      return {
        id: bloco.id,
        tipo: 'paragrafo',
        trechos: bloco.trechos.map(mapTrecho),
        ...comAlinhamento(bloco.alinhamento),
      };
    case 'citacao':
      return {
        id: bloco.id,
        tipo: 'citacao',
        trechos: bloco.trechos.map(mapTrecho),
        ...comAlinhamento(bloco.alinhamento),
      };
    case 'destaque':
      return {
        id: bloco.id,
        tipo: 'destaque',
        paragrafos: bloco.paragrafos.map((paragrafo) => paragrafo.map(mapTrecho)),
      };
    case 'htmlBruto':
      return { id: bloco.id, tipo: 'htmlBruto', html: bloco.html };
  }
};

const mapCapitulo = (capitulo: CapituloDto): CapituloSentenca => ({
  id: capitulo.id,
  titulo: capitulo.titulo,
  ...(capitulo.formatoTitulo === undefined
    ? {}
    : {
        formatoTitulo: {
          nivel: capitulo.formatoTitulo.nivel,
          alinhamento: capitulo.formatoTitulo.alinhamento,
        },
      }),
  blocos: capitulo.blocos.map(mapBloco),
});

export function mapSentencaArbitral(dto: SentencaArbitralDto): SentencaArbitralDados {
  return {
    processoId: dto.processoId,
    numeroProcesso: dto.numeroProcesso,
    tipoDecisao: dto.tipoDecisao,
    proferidaEm: dto.proferidaEm,
    codigoAutenticacao: dto.codigoAutenticacao,
    tituloTextoOficial: dto.tituloTextoOficial,
    identificacao: {
      localSentenca: dto.identificacao.localSentenca,
      instituicaoArbitral: dto.identificacao.instituicaoArbitral,
      requerimentoNumero: dto.identificacao.requerimentoNumero,
      termoArbitragemId: dto.identificacao.termoArbitragemId,
      proferidaEm: dto.identificacao.proferidaEm,
      profissionalResponsavel: dto.identificacao.profissionalResponsavel,
      plataformaUrl: dto.identificacao.plataformaUrl,
      plataformaUrlExibicao: dto.identificacao.plataformaUrlExibicao,
      autenticidadeUrl: dto.identificacao.autenticidadeUrl,
      autenticidadeUrlExibicao: dto.identificacao.autenticidadeUrlExibicao,
    },
    gruposPartes: dto.gruposPartes.map((grupo) => ({
      id: grupo.id,
      titulo: grupo.titulo,
      destaque: grupo.destaque,
      observacao: grupo.observacao,
      partes: grupo.partes.map((parte) => ({
        id: parte.id,
        nome: parte.nome,
        papelLabel: parte.papelLabel,
        papelDestacado: parte.papelDestacado,
        contatos: parte.contatos,
        documento: parte.documento,
      })),
    })),
    resumoDecisao: dto.resumoDecisao.map((bloco) => ({
      id: bloco.id,
      icone: bloco.icone,
      titulo: bloco.titulo,
      trechos: bloco.trechos.map(mapTrecho),
    })),
    qualificacoes: dto.qualificacoes.map((qualificacao) => ({
      rotulo: qualificacao.rotulo,
      texto: qualificacao.texto,
      tom: qualificacao.tom,
    })),
    capitulos: dto.capitulos.map(mapCapitulo),
    assinatura: {
      nomeManuscrito: dto.assinatura.nomeManuscrito,
      nomeFormal: dto.assinatura.nomeFormal,
      cargo: dto.assinatura.cargo,
      assinadoEm: dto.assinatura.assinadoEm,
    },
    linhaTempo: dto.linhaTempo.map((evento) => ({
      id: evento.id,
      ocorridoEm: evento.ocorridoEm,
      titulo: evento.titulo,
      descricao: evento.descricao,
      marco: evento.marco,
    })),
    glossario: dto.glossario.map((termo) => ({
      termo: termo.termo,
      explicacao: termo.explicacao,
    })),
    orientacaoDuvida: {
      titulo: dto.orientacaoDuvida.titulo,
      texto: dto.orientacaoDuvida.texto,
    },
    instituicao: {
      orgao: dto.instituicao.orgao,
      logoUrl: dto.instituicao.logoUrl,
      contatoEmail: dto.instituicao.contatoEmail,
      contatoSite: dto.instituicao.contatoSite,
      assinaturaImagemUrl: dto.instituicao.assinaturaImagemUrl,
      assinaturaCargo: dto.instituicao.assinaturaCargo,
      rodapeLegal: dto.instituicao.rodapeLegal,
    },
  };
}
